<template>
  <section class="space-y-5">
    <div>
      <h3 class="text-base font-semibold text-slate-900">日报评价</h3>
      <p class="mt-1 text-sm text-slate-500">评价用于反馈工作过程与结果，不会改变日报的提交状态。</p>
    </div>

    <div>
        <span class="form-label">评分 <span class="text-red-500">*</span></span>
        <div class="mt-2 flex items-center gap-0.5" role="radiogroup" aria-label="评分" @mouseleave="hoveredScore = 0">
          <button
            v-for="option in gradeOptions"
            :key="option.grade"
            type="button"
            class="rounded-lg p-1 transition-all duration-150 hover:scale-110 focus:outline-none"
            :class="option.score <= previewScore ? 'text-amber-400' : 'text-slate-200 hover:text-amber-300'"
            :aria-label="`${option.score} 星`"
            :aria-checked="option.score === score"
            :disabled="disabled"
            role="radio"
            @mouseenter="hoveredScore = option.score"
            @click="score = option.score"
          >
            <StarIcon class="h-8 w-8" aria-hidden="true" />
          </button>
          <span class="ml-3 rounded-md bg-slate-100 px-2.5 py-1 text-sm font-medium text-slate-700">{{ score ? `${score} / 5` : '未评分' }}</span>
        </div>
    </div>

    <label class="block">
      <span class="form-label">评价意见</span>
      <textarea v-model="comment" class="form-textarea mt-1 w-full" rows="4" placeholder="填写可执行的反馈" :disabled="disabled"></textarea>
    </label>

    <label v-if="isLowScore" class="block">
      <span class="form-label">改进建议 <span class="text-red-500">*</span></span>
      <textarea v-model="improvementSuggestion" class="form-textarea mt-1 w-full" rows="4" placeholder="低分评价必须填写改进建议" :disabled="disabled"></textarea>
    </label>

    <div class="flex justify-end">
      <button type="button" class="btn-primary" :disabled="submitting || disabled" @click="submitEvaluation">
        {{ submitting ? '提交中' : '提交评价' }}
      </button>
    </div>
  </section>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import { ElMessage } from 'element-plus'
import { StarIcon } from '@heroicons/vue/24/solid'

const props = defineProps({
  report: {
    type: Object,
    default: () => ({})
  },
  submitting: {
    type: Boolean,
    default: false
  },
  disabled: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['submit'])
const gradeOptions = [
  { score: 1, grade: 'UNQUALIFIED' },
  { score: 2, grade: 'NEEDS_IMPROVEMENT' },
  { score: 3, grade: 'MEETS_EXPECTATIONS' },
  { score: 4, grade: 'GOOD' },
  { score: 5, grade: 'EXCELLENT' }
]
const score = ref(0)
const hoveredScore = ref(0)
const comment = ref('')
const improvementSuggestion = ref('')
const previewScore = computed(() => hoveredScore.value || score.value)
const selectedGrade = computed(() => gradeOptions.find((option) => option.score === score.value))
const isLowScore = computed(() => score.value <= 2 && score.value > 0)

watch(() => props.report, (report) => {
  const evaluation = currentEvaluationOf(report)
  score.value = gradeOptions.find((option) => option.grade === evaluation.grade)?.score || 0
  comment.value = evaluation.comment || ''
  improvementSuggestion.value = ''
}, { immediate: true })

function currentEvaluationOf(report) {
  const evaluations = Array.isArray(report?.evaluations) ? report.evaluations : []
  return evaluations.find((evaluation) => evaluation.current && evaluation.phase === 'SUMMARY')
    || {}
}

function submitEvaluation() {
  if (!score.value || !selectedGrade.value) {
    ElMessage.warning('请选择评分')
    return
  }
  if (isLowScore.value && !improvementSuggestion.value.trim()) {
    ElMessage.warning('低分评价必须填写改进建议')
    return
  }

  const normalizedComment = isLowScore.value
    ? [comment.value.trim(), `改进建议：${improvementSuggestion.value.trim()}`].filter(Boolean).join('\n')
    : comment.value.trim()

  emit('submit', {
    phase: 'SUMMARY',
    grade: selectedGrade.value.grade,
    comment: normalizedComment
  })
}
</script>
